import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { surveyInstanceApi } from '../api/surveyInstanceApi';
import { CreateSurveyInstanceDTO } from '../dtos';

// Query key factory
export const surveyInstanceKeys = {
  all: ['surveyInstances'] as const,
  lists: () => [...surveyInstanceKeys.all, 'list'] as const,
  list: (surveyId: string, limit?: number) =>
    [...surveyInstanceKeys.lists(), surveyId, limit] as const,
  details: () => [...surveyInstanceKeys.all, 'detail'] as const,
  detail: (surveyId: string, instanceId: string) =>
    [...surveyInstanceKeys.details(), surveyId, instanceId] as const,
  stats: (surveyId: string) => [...surveyInstanceKeys.all, 'stats', surveyId] as const,
};

// Get instances for a survey
export const useSurveyInstances = (surveyId: string, limit: number = 5) => {
  return useQuery({
    queryKey: surveyInstanceKeys.list(surveyId, limit),
    queryFn: () => surveyInstanceApi.getBySurveyId(surveyId, limit),
    enabled: !!surveyId,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });
};

// Get specific instance
export const useSurveyInstance = (surveyId: string, instanceId: string) => {
  return useQuery({
    queryKey: surveyInstanceKeys.detail(surveyId, instanceId),
    queryFn: () => surveyInstanceApi.getById(surveyId, instanceId),
    enabled: !!surveyId && !!instanceId,
    staleTime: 60000, // 1 minute
  });
};

// Get instance statistics
export const useSurveyInstanceStats = (surveyId: string) => {
  return useQuery({
    queryKey: surveyInstanceKeys.stats(surveyId),
    queryFn: () => surveyInstanceApi.getStats(surveyId),
    enabled: !!surveyId,
    staleTime: 30000, // 30 seconds
  });
};

// Create instance mutation
export const useCreateSurveyInstance = (surveyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSurveyInstanceDTO) => surveyInstanceApi.create(surveyId, data),
    onSuccess: () => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: surveyInstanceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: surveyInstanceKeys.stats(surveyId) });
    },
    onError: (error) => {
      console.error('Error creating survey instance:', error);
    },
  });
};
